import { RoomMixerStreamEntityRaw, ROOM_LIVE_STATUS } from "@common/domain";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action, createReducer, on } from "@ngrx/store";
import { RoomMixerJson, RoomMixerVO } from "@youm-front/room/domain";
import { RoomMixerActions, RoomMixerStreamActions } from "../actions";
import { RoomLiveStreamStatusEvents, RoomMixerEvents } from "../events";

export const ROOM_MIXER_FEATURE_KEY = "roomMixer";

export interface RoomMixerState extends EntityState<Record<string, never>> {
  roomMixer: null | RoomMixerJson;
  states: RoomMixerVO[];
  stream: null | RoomMixerStreamEntityRaw;
}

export const roomMixerAdapter: EntityAdapter<Record<string, never>> =
  createEntityAdapter();

const initialState: RoomMixerState = roomMixerAdapter.getInitialState({
  roomMixer: null,
  states: [],
  stream: null
});

const reducer = createReducer(
  initialState,
  on(RoomMixerActions.roomMixerDelete, () => initialState),
  on(RoomMixerActions.roomMixerStatesShift, (state) => {
    return {
      ...state,
      states: state.states.slice(1)
    };
  }),
  on(
    RoomMixerEvents.roomMixerErrorEvent,
    (
      state,
      {
        roomMixerErrorEventVO: {
          value: { roomId }
        }
      }
    ) => {
      const roomMixerVO = new RoomMixerVO({
        roomId,
        mixerId: null,
        roomMixerStart: false,
        roomMixerStarted: false
      });

      return {
        ...state,
        roomMixer: roomMixerVO.toJSON(),
        states: [...state.states, roomMixerVO]
      };
    }
  ),
  on(
    RoomMixerEvents.roomMixerStartEvent,
    (
      state,
      {
        roomMixerStartEventVO: {
          value: { roomId }
        }
      }
    ) => {
      const roomMixerVO = new RoomMixerVO({
        roomId,
        mixerId: null,
        roomMixerStart: true,
        roomMixerStarted: false
      });

      return {
        ...state,
        roomMixer: roomMixerVO.toJSON(),
        states: [...state.states, roomMixerVO]
      };
    }
  ),
  on(
    RoomMixerEvents.roomMixerStartedEvent,
    (
      state,
      {
        roomMixerStartedEventVO: {
          value: { roomId, mixerId }
        }
      }
    ) => {
      const roomMixerVO = new RoomMixerVO({
        roomId,
        mixerId,
        roomMixerStart: false,
        roomMixerStarted: true
      });

      return {
        ...state,
        roomMixer: roomMixerVO.toJSON(),
        states: [...state.states, roomMixerVO]
      };
    }
  ),
  on(
    RoomMixerEvents.roomMixerToRotateEvent,
    (
      state,
      {
        roomMixerToRotateEventVO: {
          value: { roomId }
        }
      }
    ) => {
      const roomMixerVO = new RoomMixerVO({
        roomId,
        mixerId: null,
        roomMixerStart: false,
        roomMixerStarted: false
      });

      return {
        ...state,
        roomMixer: roomMixerVO.toJSON(),
        states: [...state.states, roomMixerVO]
      };
    }
  ),
  on(RoomLiveStreamStatusEvents.update, (state, { roomId, status }) => {
    const roomMixerVO = new RoomMixerVO({
      roomId,
      mixerId: null,
      roomMixerStart: status === ROOM_LIVE_STATUS.START,
      roomMixerStarted: status === ROOM_LIVE_STATUS.STARTED
    });

    return {
      ...state,
      roomMixer: roomMixerVO.toJSON(),
      states: [...state.states, roomMixerVO]
    };
  }),
  on(
    RoomMixerStreamActions.reset,
    RoomMixerStreamActions.roomMixerStreamGet,
    RoomMixerStreamActions.roomMixerStreamGetFailure,
    (state) => ({
      ...state,
      stream: null
    })
  ),
  on(RoomMixerStreamActions.roomMixerStreamGetSuccess, (state, action) => ({
    ...state,
    stream: action.stream?.toJSON() || null
  }))
);

export function roomMixerReducer(
  state: RoomMixerState | undefined,
  action: Action
) {
  return reducer(state, action);
}
